import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import grid from "../assets/images/grid_lines.png";
import DemoImg from "../assets/images/demo.gif";
import tutorImg from "../assets/images/tutor.png";
import Learning from "../assets/images/Start learning.png";
import DashboardImg from "../assets/images/image.png";
import LoadingScreen from "../components/loader";
import CardSection from "../components/CardSection";
import AnimatedCard from "../components/AnimatedCard";
import TicketCardSection from "../components/TicketCardSection";
import StartTrial from "../components/StartTrial";
import ScreenImg from "../assets/images/screen.svg";
import SmartCard from "../components/SmartCard";
import PersonalTutorSection from "../components/PersonalTutorSection";
import getYouTubeEmbedUrl from "../utils/videoUrlExtractor";
import TutorFeatureCarousel from "../components/TutorFeatureCarousel";
import PioneerImg from "../assets/images/pioneerImg.jpg";
import BlogCard from "../components/BlogCard";
import FAQSection from "../components/Faq";
import GetInTouchSection from "../components/GetInTouch";
import Footer from "../components/Footer";

const Home = () => {
  const navigate = useNavigate();
  const [contentLoaded, setContentLoaded] = useState(false);
  const [loaderComplete, setLoaderComplete] = useState(false);
  const tabletSectionRef = useRef(null);

  // Scroll hook for tablet animation, scoped to tabletSectionRef
  const { scrollYProgress } = useScroll({
    target: tabletSectionRef,
    offset: ["start end", "end start"],
  });

  // Debug scrollYProgress (remove after testing)
  // useEffect(() => {
  //   scrollYProgress.onChange((value) =>
  //     // console.log("tablet scrollYProgress:", value)
  //   );
  // }, [scrollYProgress]);

  useEffect(() => {
    const handleLoaderComplete = () => {
      setLoaderComplete(true);
      setTimeout(() => {
        setContentLoaded(true);
      }, 300);
    };

    window.addEventListener("loaderComplete", handleLoaderComplete);
    const fallbackTimer = setTimeout(() => {
      if (!loaderComplete) {
        setLoaderComplete(true);
        setContentLoaded(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener("loaderComplete", handleLoaderComplete);
      clearTimeout(fallbackTimer);
    };
  }, [loaderComplete]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.removedNodes.length > 0) {
          if (!document.querySelector('[data-loader="true"]')) {
            setLoaderComplete(true);
            setTimeout(() => {
              setContentLoaded(true);
            }, 300);
          }
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Adjusted scroll ranges for tablet animation
  const tabletScale = useTransform(scrollYProgress, [0.1, 0.3], [0.8, 1]);
  const tabletX = useTransform(scrollYProgress, [0.1, 0.2], ["-13%", "30%"]);
  const tabletY = useTransform(scrollYProgress, [0.1, 0.1], ["-180%", "0%"]);
  const tabletOpacity = useTransform(scrollYProgress, [0.1, 0.25], [1, 1]);
  const tabletRotateY = useTransform(scrollYProgress, [0.1, 0.3], [30, 0]);
  const tabletTop = useTransform(scrollYProgress, [0, 0.1], [0, 100]);

  // Animation variants
  const leftEntryVariants = {
    hidden: { x: -100, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const rightEntryVariants = {
    hidden: { x: 100, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.4,
      },
    },
  };

  const supportData = [
    "Tracking students progress in real time- identifying who needs help instantly",
    "Providing AI-driven performance insights- see patterns in students learning",
    "Generating smart lesson plans- Save time with AI-powered resources",
    "Offering personalized student support- ensure every learner gets what they need",
  ];

  return (
    <>
      <LoadingScreen />
      <AnimatePresence>
        {loaderComplete && (
          <>
            <motion.div
              className="w-full relative h-fit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute inset-0  gradient-primary"></div>
              <div className="absolute inset-0 opacity-30  h-full w-full bg-[url('./assets/images/grid_lines.png')] bg-repeat-y bg-contain bg-center"></div>

              {/* Hero Section */}
              <section className="pt-32 pb-0 px-4 w-full relative overflow-hidden z-[10] bg-transparent">
                <div className="max-w-screen-xl mx-auto">
                  <div className="flex flex-col md:flex-row gap-12 items-center">
                    <motion.div
                      className="text-left w-full md:w-[70%]"
                      initial="hidden"
                      animate={contentLoaded ? "visible" : "hidden"}
                      variants={leftEntryVariants}
                    >
                      <h1 className="text-2xl md:text-5xl text-primary-purple-200 font-bold mb-4">
                        Bildup is a{" "}
                        <span className="text-primary-700">
                          Super-Intelligent
                        </span>
                        <br />
                        <span className="flex items-center gap-2 mt-2">
                          AI{" "}
                          <Icon
                            icon="game-icons:robot-head"
                            className="inline-block text-4xl text-primary-700"
                          />
                          <img src={tutorImg} alt="tutor" className="md:w-20 md:h-20 w-12 h-12" /> Personal Tutor
                        </span>
                      </h1>
                      <p className="text-lg text-gray-600 mb-8">
                        A supportive expert by your side, ready to guide you
                        24/7
                      </p>
                     <StartTrial/>

                      {/* Mobile View */}

                      <motion.div
                        className="relative w-full md:w-[50%] md:hidden block mt-3"
                        initial="hidden"
                        animate={contentLoaded ? "visible" : "hidden"}
                        variants={rightEntryVariants}
                      >
                        <div className="bg-gradient-to-r from-teal-400 to-purple-500 rounded-3xl p-4 overflow-hidden shadow-xl">
                          <img
                            src={DemoImg}
                            alt="AI Tutor Demo"
                            className="rounded-2xl w-full"
                          />
                        </div>
                        <motion.div
                          className="absolute -top-6 -right-6 bg-indigo-100 rounded-full py-1 px-3 shadow-md"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={
                            contentLoaded
                              ? { opacity: 1, scale: 1 }
                              : { opacity: 0, scale: 0.8 }
                          }
                          transition={{ delay: 0.6, duration: 0.5 }}
                        >
                          <p className="text-xs font-medium">AI Powered</p>
                        </motion.div>
                        <motion.div
                          className="absolute -bottom-4 -left-4 bg-purple-100 rounded-full py-1 px-3 shadow-md"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={
                            contentLoaded
                              ? { opacity: 1, scale: 1 }
                              : { opacity: 0, scale: 0.8 }
                          }
                          transition={{ delay: 0.7, duration: 0.5 }}
                        >
                          <p className="text-xs font-medium">Support</p>
                        </motion.div>
                        <motion.div
                          className="absolute top-1/4 -right-8 bg-rose-100 rounded-full py-1 px-3 shadow-md"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={
                            contentLoaded
                              ? { opacity: 1, scale: 1 }
                              : { opacity: 0, scale: 0.8 }
                          }
                          transition={{ delay: 0.8, duration: 0.5 }}
                        >
                          <p className="text-xs font-medium">Growth</p>
                        </motion.div>
                        <motion.div
                          className="absolute bottom-1/3 -left-8 bg-teal-100 rounded-full py-1 px-3 shadow-md"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={
                            contentLoaded
                              ? { opacity: 1, scale: 1 }
                              : { opacity: 0, scale: 0.8 }
                          }
                          transition={{ delay: 0.9, duration: 0.5 }}
                        >
                          <p className="text-xs font-medium">Personalized</p>
                        </motion.div>
                      </motion.div>

                      {/*  */}
                      <div className="flex flex-col mt-16 md:flex-row items-center justify-between md:gap-3 gap-1">
                        <div className="h-full md:w-[50%]">
                          <img
                            src={Learning}
                            alt="Learning"
                            className="w-full h-full"
                          />
                        </div>
                        <div className="flex items-center bg-white p-3 rounded-full md:w-[50%] shadow-lg">
                          <div className="bg-amber-100 p-2 rounded-full  mr-2">
                            <Icon
                              icon="ph:trophy-fill"
                              className="text-amber-500 w-6 h-6"
                            />
                          </div>
                          <div className="text-left flex">

                            <p className="text-primary-purple-200 font-semibold text-[14px]">
                              #1 Africa’s 1st Super Intelligent AI Tutor
                            </p>
                          </div>
                        </div>
                      </div>
                      <motion.div
                        style={{ backgroundImage: `url(${DashboardImg})` }}
                        className="rounded-full bg-transparent h-20 md:mt-5 mt-2  items-center justify-center bg-cover bg-center bg-no-repeat hidden md:flex"
                        initial="hidden"
                        animate={contentLoaded ? "visible" : "hidden"}
                        variants={fadeInVariants}
                      >
                        <button
                          className="absolute bg-white/80 hover:bg-white text-primary-700 font-medium rounded-full p-4 backdrop-blur-sm flex items-center transition-all duration-300"
                          onClick={() => {
                            const el = document.getElementById("tabFrame");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                          }}
                        >
                          <Icon icon="ph:play-fill" className="w-8 h-8" />
                        </button>
                      </motion.div>
                    </motion.div>

                    <motion.div
                      className="relative w-full md:w-[50%] hidden md:block"
                      initial="hidden"
                      animate={contentLoaded ? "visible" : "hidden"}
                      variants={rightEntryVariants}
                    >
                      <div className="bg-gradient-to-r from-teal-400 to-purple-500 rounded-3xl p-4 overflow-hidden shadow-xl">
                        <img
                          src={DemoImg}
                          alt="AI Tutor Demo"
                          className="rounded-2xl w-full"
                        />
                      </div>
                      <motion.div
                        className="absolute -top-6 -right-6 bg-indigo-100 rounded-full py-1 px-3 shadow-md"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={
                          contentLoaded
                            ? { opacity: 1, scale: 1 }
                            : { opacity: 0, scale: 0.8 }
                        }
                        transition={{ delay: 0.6, duration: 0.5 }}
                      >
                        <p className="text-xs font-medium">AI Powered</p>
                      </motion.div>
                      <motion.div
                        className="absolute -bottom-4 -left-4 bg-purple-100 rounded-full py-1 px-3 shadow-md"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={
                          contentLoaded
                            ? { opacity: 1, scale: 1 }
                            : { opacity: 0, scale: 0.8 }
                        }
                        transition={{ delay: 0.7, duration: 0.5 }}
                      >
                        <p className="text-xs font-medium">Support</p>
                      </motion.div>
                      <motion.div
                        className="absolute top-1/4 -right-8 bg-rose-100 rounded-full py-1 px-3 shadow-md"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={
                          contentLoaded
                            ? { opacity: 1, scale: 1 }
                            : { opacity: 0, scale: 0.8 }
                        }
                        transition={{ delay: 0.8, duration: 0.5 }}
                      >
                        <p className="text-xs font-medium">Growth</p>
                      </motion.div>
                      <motion.div
                        className="absolute bottom-1/3 -left-8 bg-teal-100 rounded-full py-1 px-3 shadow-md"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={
                          contentLoaded
                            ? { opacity: 1, scale: 1 }
                            : { opacity: 0, scale: 0.8 }
                        }
                        transition={{ delay: 0.9, duration: 0.5 }}
                      >
                        <p className="text-xs font-medium">Personalized</p>
                      </motion.div>
                    </motion.div>
                  </div>
                </div>
              </section>

              {/* Tablet Section */}
              <section
                // ref={tabletSectionRef}
                className="md:py-32 py-10 px-4 relative md:h-[150vh] overflow-hidden"
                style={{ zIndex: 30 }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-300 to-transparent pointer-events-none"></div>
                <div className="max-w-screen-xl mx-auto relative">
                  <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <h2 className="text-[22px] md:text-[32px] font-bold text-gradient mb-4">
                      We see Bildup AI as the new frontier of human-machine
                      collaboration in education, offering a potential glimpse
                      into the future of learning with AI integration
                    </h2>
                  </motion.div>
                  <motion.div
                    className="relative mx-auto"
                    // style={{
                    //   scale: tabletScale,
                    //   x: tabletX,
                    //   y: tabletY,
                    //   opacity: tabletOpacity,
                    //   rotateY: tabletRotateY,
                    //   transformOrigin: "left center",
                    //   willChange: "transform",
                    //   top: tabletTop,
                    // }}
                    style={{
                      opacity: 0,
                    }}
                    whileInView={{ opacity: 1, transition: { duration: 0.8 } }}
                    // viewport={{ once: true }}
                    id="tabFrame"
                  >
                    <div className="relative max-w-[1200px] max-h-[1100px] mx-auto -z-[999]">
                      <div className="relative overflow-hidden">
                        <svg
                          className="w-full h-auto"
                          viewBox="0 0 910 470"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 41.8C0 18.7405 18.7405 0 41.8 0H868.2C891.26 0 910 18.7405 910 41.8V428.2C910 451.26 891.26 470 868.2 470H41.8C18.7405 470 0 451.26 0 428.2V41.8Z"
                            fill="#1E1E1E"
                          />
                          <path
                            d="M26 41.8C26 33.0569 33.0569 26 41.8 26H868.2C876.943 26 884 33.0569 884 41.8V428.2C884 436.943 876.943 444 868.2 444H41.8C33.0569 444 26 436.943 26 428.2V41.8Z"
                            fill="#101827"
                          />
                          <path
                            d="M26 59H884V411H26V59Z"
                            fill="white"
                            className="tablet-screen"
                          />
                          <circle cx="455" cy="427" r="9.5" fill="#374151" />
                          <circle cx="455" cy="36" r="3.5" fill="#374151" />
                          <rect
                            x="0"
                            y="100"
                            width="4"
                            height="60"
                            rx="2"
                            fill="#404040"
                          />
                          <rect
                            x="0"
                            y="200"
                            width="4"
                            height="30"
                            rx="2"
                            fill="#404040"
                          />
                          <rect
                            x="0"
                            y="240"
                            width="4"
                            height="30"
                            rx="2"
                            fill="#404040"
                          />
                        </svg>
                        <div
                          className="absolute top-[59px] tablet-iframe left-[26px] right-[26px] bottom-[59px] bg-white overflow-hidden"
                          style={{
                            maskImage:
                              "linear-gradient(to bottom, black 99.9%, transparent 100%)",
                          }}
                        >
                          <div className="w-full h-full relative overflow-hidden">
                            <iframe
                              src={`${getYouTubeEmbedUrl(
                                "https://youtu.be/1PIcEmZUUe4"
                              )}?autoplay=0&rel=0&modestbranding=1&mute=1`}
                              className="absolute inset-0 w-full h-full object-cover "
                              style={{
                                width: "100%",
                                height: "100%",
                                position: "absolute",
                                left: "0",
                                top: "0",
                                right: "0",
                                bottom: "0",
                              }}
                              title="Tablet Content"
                              frameBorder="0"
                              loading="lazy"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  {/* <motion.div
                    className="relative mx-auto"
                    style={{
                      scale: tabletScale,
                      x: tabletX,
                      y: tabletY,
                      opacity: tabletOpacity,
                      rotateY: tabletRotateY,
                      transformOrigin: "left center",
                      willChange: "transform",
                      top: tabletTop,
                    }}
                    whileInView={{ opacity: 1, transition: { duration: 0.8 } }}
                    viewport={{ once: true }}
                    id="tabFrame"
                  >
                    <div className="relative max-w-[1200px] max-h-[1100px] mx-auto -z-[999]">
                      <div className="relative overflow-hidden">
                        <svg
                          className="w-full h-auto"
                          viewBox="0 0 910 470"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 41.8C0 18.7405 18.7405 0 41.8 0H868.2C891.26 0 910 18.7405 910 41.8V428.2C910 451.26 891.26 470 868.2 470H41.8C18.7405 470 0 451.26 0 428.2V41.8Z"
                            fill="#1E1E1E"
                          />
                          <path
                            d="M26 41.8C26 33.0569 33.0569 26 41.8 26H868.2C876.943 26 884 33.0569 884 41.8V428.2C884 436.943 876.943 444 868.2 444H41.8C33.0569 444 26 436.943 26 428.2V41.8Z"
                            fill="#101827"
                          />
                          <path
                            d="M26 59H884V411H26V59Z"
                            fill="white"
                            className="tablet-screen"
                          />
                          <circle cx="455" cy="427" r="9.5" fill="#374151" />
                          <circle cx="455" cy="36" r="3.5" fill="#374151" />
                          <rect
                            x="0"
                            y="100"
                            width="4"
                            height="60"
                            rx="2"
                            fill="#404040"
                          />
                          <rect
                            x="0"
                            y="200"
                            width="4"
                            height="30"
                            rx="2"
                            fill="#404040"
                          />
                          <rect
                            x="0"
                            y="240"
                            width="4"
                            height="30"
                            rx="2"
                            fill="#404040"
                          />
                        </svg>
                        <div
                          className="absolute top-[59px] left-[26px] right-[26px] bottom-[59px] bg-white overflow-hidden"
                          style={{
                            maskImage:
                              "linear-gradient(to bottom, black 99.9%, transparent 100%)",
                          }}
                        >
                          <div className="w-full h-full relative">
                            <iframe
                              src={`${getYouTubeEmbedUrl(
                                "https://youtu.be/yCaOUmJkbA4"
                              )}?autoplay=0&rel=0&modestbranding=1&mute=1`}
                              className="absolute inset-0 w-full h-full"
                              title="Tablet Content"
                              frameBorder="0"
                              loading="lazy"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div> */}
                </div>
              </section>

              {/* Feature Cards Section */}
              <div className="relative bg-gradient-to-b from-transparent via-white via-whilte to-white ">
                <CardSection />
              </div>

              {/* Take |Charge Section */}
              <div className="relative bg-gradient-to-t  from-white/70  to-white flex flex-col items-center justify-center pb-8">
                <h1 className="md:text-[40px] md:font-bold text-[20px]  mb-5 w-[90%] mt-0 md:mt-0  md:w-[50%] text-gradient text-center">
                  Take charge of your learning journey with smarter, more
                  focused lessons tailored to help you succeed!
                </h1>
                <StartTrial />
                <img src={ScreenImg} alt="Take Charge" />
              </div>

              {/* Tickets Card Section */}
              <div className=" relative h-fit bg-gradient-to-b  from-white/70 via-transparent to-white">
                <TicketCardSection />
              </div>

              <div className=" relative h-fit bg-gradient-to-b  from-white via-transparent   to-white pt-16 p">
                <SmartCard />
              </div>

              <div className=" relative h-fit bg-gradient-to-b  from-white via-gray-50    to-gray-100 pt-16 p">
                <PersonalTutorSection />
              </div>
              <div className=" relative h-fit bg-gradient-to-b  from-gray-100 via-gray-50    to-gray-100 pt-16 p">
                <TutorFeatureCarousel />
              </div>

              <div className=" relative h-fit bg-gradient-to-b  from-gray-100 via-gray-50    to-gray-100 pt-16 p">
                <div className="radial-bg ">
                  <div className="md:px-20 md:pt-16 px-5 pt-8">
                    <div className=" w-full flex flex-col gap-4 justify-center items-center">
                      <h1 className="md:text-[40px] text-[24px] text-center text-gradient-blue">
                        Support for Every Educator, Every Classroom
                      </h1>
                      <p className="md:text-[24px] text-[14px] md:w-[70%] text-center text-gray-500 mb-4">
                        Bildup helps you monitor student progress, personalize
                        instruction, and streamline lesson planning - so you can
                        focus on what truly matters:{" "}
                        <span className="text-primary-purple-400">
                          teaching.
                        </span>{" "}
                      </p>

                      <StartTrial text={"Unlock Early Access"} />
                    </div>
                  </div>

                  <div className="flex items-end justify-end w-full mt-8 md:px-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 w-full">
                      {supportData &&
                        supportData.map((item, index) => {
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-4 mb-3 md:p-5  p-2 bg-primary-purple-100/30 rounded-2xl "
                            >
                              <span className="bg-primary-purple-400 md:p-2 p-1 rounded">
                                <Icon
                                  icon={"streamline:check-solid"}
                                  className="md:h-6 md:w-6 w-4 h-4 text-white"
                                />
                              </span>
                              <p className="md:text-[20px] text-[16px] md:w-[70%] w-[80%] ">{item}</p>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  <div className="md:px-16 flex items-center justify-center mt-16 md:h-[70vh]">
                    <div className="bg-gradient-to-br p-16 from-purple-600 via-purple-500 to-pink-500 flex items-center justify-between w-[80%] rounded-lg">
                      <AnimatedCard>

                      <p className="text-white md:text-[40px] text-[16px]">
                        Empower growth with innovative learning methods that
                        boost retention, engagement, and performance. Backed by
                        research, our approach ensures deeper understanding and
                        lasting impact.
                      </p>
                      </AnimatedCard>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative  bg-gradient-to-b  from-gray-50 via-gray-50    to-gray-100 pt-16 md:px-24 px-5 ">
                <div className="flex flex-col items-center justify-center gap-4 mb-10">
                  <h1 className="md:text-[35px] text-[20px] font-bold md:w-[50%] text-center  ">
                    {" "}
                    Pioneering Digital Learning Tool
                  </h1>
                  <p className="text-gray-500 md:text-[20px] text-[16px] md:w-[50%] text-center">
                    Bildup is a pioneer in leveraging AI integration in
                    transforming education in Africa, empowering learners to
                    overcome limitations and unlock their full potential.
                  </p>
                  <div className="w-[70vh] h-[35vh] ">

                    <img
                      src={PioneerImg}
                      alt=""
                      className="rounded-lg w-full h-full object-cover object-center "
                    />
                  </div>
                </div>
                <div className="grid grid-col-1  gap-4 md:grid-cols-3 h-full">
                  <AnimatedCard
                    className="flex flex-col gap-3"
                    direction="left"
                  >
                    <div className="bg-purple-200 h-[25vh] md:h-full p-10 rounded-md flex flex-col gap-3 items-center justify-center">
                      <h1 className="text-[24px] font-bold text-center">
                        Hyper-Personalization
                      </h1>
                      <p className="text-gray-500 text-[20px] text-center">
                        Adapts to each student's learning style, pace, and
                        strengths.
                      </p>
                    </div>
                    <div className="bg-pink-300 p-10 h-[25vh] md:h-full rounded-md flex flex-col gap-3 items-center justify-center">
                      <h1 className="text-[24px] font-bold text-center">
                        24/7 Standby Tutor
                      </h1>
                      <p className="text-gray-500 text-[20px] text-center">
                        Learning never stops - Bildup AI is always available for
                        students.
                      </p>
                    </div>
                  </AnimatedCard>
                  <AnimatedCard
                    direction="bottom"
                    className="flex items-center flex-col h-[25vh] md:h-full justify-center px-6 bg-black rounded-md"
                  >
                    <h1 className="text-[24px] font-bold text-center text-white">
                      Instant Insights
                    </h1>
                    <p className="text-white text-[20px] text-center">
                      Provides teachers and parents with real-time updates and
                      analytics.
                    </p>
                  </AnimatedCard>
                  <AnimatedCard
                    direction="right"
                    className="flex flex-col gap-3"
                  >
                    <div className="bg-orange-200 h-[25vh] md:h-full p-10 rounded-md flex flex-col gap-3 items-center justify-center">
                      <h1 className="text-[24px] font-bold text-center">
                        Inclusive Learning
                      </h1>
                      <p className="text-gray-500 text-[20px] text-center">
                        Accessible for all students, anywhere in the world.
                      </p>
                    </div>
                    <div className="bg-gray-300 h-[25vh] md:h-full p-10 rounded-md flex flex-col gap-3 items-center justify-center">
                      <h1 className="text-[24px] font-bold text-center">
                        Immersive Education
                      </h1>
                      <p className="text-gray-500 text-[20px] text-center">
                        Blends AI with interactive tools for deeper
                        understanding.
                      </p>
                    </div>
                  </AnimatedCard>
                </div>
              </div>

              {/* <div className=" relative h-fit bg-gradient-to-b  from-white via-transparent   to-white pt-16 p">
                <BlogCard />
              </div> */}
              <div className=" relative h-fit bg-gradient-to-b  from-white via-transparent   to-white pt-16 p">
                <FAQSection />
              </div>

              <div className=" relative h-fit bg-gradient-to-b  from-white    to-white">
                <GetInTouchSection />
              </div>
            </motion.div>

            <motion.div className="relative bg-transparent ">
              <Footer />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;
